import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { 
  Database, 
  FolderOpen, 
  FileText, 
  ArrowRight, 
  ExternalLink,
  Handshake,
  Library,
  List,
  FileCode,
  Tag,
  BarChart3,
  MessageSquare,
  Lightbulb,
  CheckCircle2,
  AlertCircle,
  Clock,
  Hammer,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { DataStandardsManager } from '@/components/admin/DataStandardsManager';
import { CategoriesManager } from '@/components/admin/CategoriesManager';
import { DocCategoriesManager } from '@/components/admin/DocCategoriesManager';
import { DocsManager } from '@/components/admin/DocsManager';
import { PartnersManager } from '@/components/admin/PartnersManager';
import { ContactSubmissionsManager } from '@/components/admin/ContactSubmissionsManager';
import { SeoManager } from '@/components/admin/SeoManager';
import { SiteSettingsManager } from '@/components/admin/SiteSettingsManager';
import { FieldLibraryManager } from '@/components/admin/FieldLibraryManager';
import { LookupsManager } from '@/components/admin/LookupsManager';
import { DataTypeDocsManager } from '@/components/admin/DataTypeDocsManager';
import ProposalsManager from '@/components/admin/ProposalsManager';
import { AnalyticsManager } from '@/components/admin/AnalyticsManager';
import { AdfElementsManager } from '@/components/admin/AdfElementsManager';
import { AdfTemplatesManager } from '@/components/admin/AdfTemplatesManager';
import AdfChangelogManager from '@/components/admin/AdfChangelogManager';
import { UsersManager } from '@/components/admin/UsersManager';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { InviteAdminDialog } from '@/components/admin/InviteAdminDialog';
import { NoIndex } from '@/components/shared/NoIndex';
import { formatRelativeDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import Account from './Account';

interface DashboardStats {
  // Standards
  apiStandards: number;
  adfElements: number;
  categories: number;
  
  // Data Architecture
  fieldLibrary: number;
  lookupGroups: number;
  lookupValues: number;
  dataTypes: number;
  
  // Documentation
  docArticles: number;
  docCategories: number;
  
  // Action Items
  newSubmissions: number;
  pendingProposals: number;
  
  // Partners
  partners: number;
  
  // Activity
  lastUpdates: {
    table: string;
    label: string;
    icon: React.ElementType;
    updatedAt: string | null;
  }[];
}

const defaultStats: DashboardStats = {
  apiStandards: 0,
  adfElements: 0,
  categories: 0,
  fieldLibrary: 0,
  lookupGroups: 0,
  lookupValues: 0,
  dataTypes: 0,
  docArticles: 0,
  docCategories: 0,
  newSubmissions: 0,
  pendingProposals: 0,
  partners: 0,
  lastUpdates: [],
};

// Extract section from URL path
function getActiveSectionFromPath(pathname: string): string {
  const path = pathname.replace('/dashboard', '').replace(/^\//, '');
  return path || 'overview';
}

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState(true);

  // Derive active section from URL
  const activeSection = getActiveSectionFromPath(location.pathname);

  // Navigate to section via URL
  const handleSectionChange = (section: string) => {
    if (section === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${section}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Parallel fetch for all counts
        const [
          { count: apiStandards },
          { count: adfElements },
          { count: categories },
          { count: fieldLibrary },
          { data: lookupsData },
          { count: dataTypes },
          { count: docArticles },
          { count: docCategories },
          { count: newSubmissions },
          { count: pendingProposals },
          { count: partners },
          // Fetch last updates for each table
          { data: standardsUpdate },
          { data: adfUpdate },
          { data: fieldUpdate },
          { data: lookupsUpdate },
          { data: docsUpdate },
          { data: dataTypesUpdate },
        ] = await Promise.all([
          supabase.from('data_standards').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('adf_elements').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('categories').select('*', { count: 'exact', head: true }),
          supabase.from('field_library').select('*', { count: 'exact', head: true }),
          supabase.from('lookups').select('lookup_group, id').eq('is_active', true),
          supabase.from('data_type_docs').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('documentation').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('doc_categories').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('contact_submissions').select('*', { count: 'exact', head: true }).eq('status', 'new'),
          supabase.from('standard_proposals').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('partners').select('*', { count: 'exact', head: true }).eq('is_active', true),
          // Get most recent update from each table
          supabase.from('data_standards').select('updated_at').order('updated_at', { ascending: false }).limit(1),
          supabase.from('adf_elements').select('updated_at').order('updated_at', { ascending: false }).limit(1),
          supabase.from('field_library').select('updated_at').order('updated_at', { ascending: false }).limit(1),
          supabase.from('lookups').select('updated_at').order('updated_at', { ascending: false }).limit(1),
          supabase.from('documentation').select('updated_at').order('updated_at', { ascending: false }).limit(1),
          supabase.from('data_type_docs').select('updated_at').order('updated_at', { ascending: false }).limit(1),
        ]);

        // Calculate unique lookup groups
        const uniqueGroups = new Set(lookupsData?.map(l => l.lookup_group) || []);

        // Build last updates array
        const lastUpdates: DashboardStats['lastUpdates'] = [
          { table: 'documentation', label: 'Documentation', icon: FileText, updatedAt: docsUpdate?.[0]?.updated_at || null },
          { table: 'adf_elements', label: 'ADF Elements', icon: Tag, updatedAt: adfUpdate?.[0]?.updated_at || null },
          { table: 'field_library', label: 'Field Library', icon: Library, updatedAt: fieldUpdate?.[0]?.updated_at || null },
          { table: 'lookups', label: 'Lookups', icon: List, updatedAt: lookupsUpdate?.[0]?.updated_at || null },
          { table: 'data_type_docs', label: 'Data Types', icon: FileCode, updatedAt: dataTypesUpdate?.[0]?.updated_at || null },
          { table: 'data_standards', label: 'API Standards', icon: Database, updatedAt: standardsUpdate?.[0]?.updated_at || null },
        ].sort((a, b) => {
          if (!a.updatedAt) return 1;
          if (!b.updatedAt) return -1;
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });

        setStats({
          apiStandards: apiStandards || 0,
          adfElements: adfElements || 0,
          categories: categories || 0,
          fieldLibrary: fieldLibrary || 0,
          lookupGroups: uniqueGroups.size,
          lookupValues: lookupsData?.length || 0,
          dataTypes: dataTypes || 0,
          docArticles: docArticles || 0,
          docCategories: docCategories || 0,
          newSubmissions: newSubmissions || 0,
          pendingProposals: pendingProposals || 0,
          partners: partners || 0,
          lastUpdates,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderContent = () => {
    switch (activeSection) {
      case 'standards':
        return <DataStandardsManager />;
      case 'categories':
        return <CategoriesManager />;
      case 'partners':
        return <PartnersManager />;
      case 'field-library':
        return <FieldLibraryManager />;
      case 'lookups':
        return <LookupsManager />;
      case 'doc-categories':
        return <DocCategoriesManager />;
      case 'docs':
        return <DocsManager />;
      case 'data-type-docs':
        return <DataTypeDocsManager />;
      case 'adf-elements':
        return <AdfElementsManager />;
      case 'adf-templates':
        return <AdfTemplatesManager />;
      case 'adf-changelog':
        return <AdfChangelogManager />;
      case 'users':
        return <UsersManager />;
      case 'analytics':
        return <AnalyticsManager />;
      case 'proposals':
        return <ProposalsManager />;
      case 'submissions':
        return <ContactSubmissionsManager />;
      case 'seo':
        return <SeoManager />;
      case 'settings':
        return <SiteSettingsManager />;
      case 'account':
        return <Account />;
      default:
        return (
          <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Overview</h1>
                <p className="text-muted-foreground">Welcome back, {user?.email}</p>
              </div>
              <InviteAdminDialog />
            </div>

            {/* Platform Stats - Compact Row */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">PLATFORM STATS</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard 
                  icon={Database} 
                  value={stats.apiStandards} 
                  label="API Standards" 
                  loading={loading} 
                />
                <StatCard 
                  icon={Tag} 
                  value={stats.adfElements} 
                  label="ADF Elements" 
                  loading={loading} 
                />
                <StatCard 
                  icon={Library} 
                  value={stats.fieldLibrary} 
                  label="Field Library" 
                  loading={loading} 
                />
                <StatCard 
                  icon={List} 
                  value={stats.lookupGroups} 
                  label="Lookup Groups" 
                  loading={loading} 
                />
                <StatCard 
                  icon={FileCode} 
                  value={stats.dataTypes} 
                  label="Data Types" 
                  loading={loading} 
                />
                <StatCard 
                  icon={FileText} 
                  value={stats.docArticles} 
                  label="Guide Articles" 
                  loading={loading} 
                />
              </div>
            </div>

            {/* Recent Activity & Action Required */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1">
                    {loading ? (
                      <p className="text-sm text-muted-foreground">Loading...</p>
                    ) : (
                      stats.lastUpdates.map((item) => (
                        <div 
                          key={item.table} 
                          className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <item.icon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.label}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {item.updatedAt ? formatRelativeDate(item.updatedAt) : 'Never'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Action Required */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Action Required
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <ActionItem 
                      label="New contact submissions"
                      count={stats.newSubmissions}
                      onClick={() => handleSectionChange('submissions')}
                      loading={loading}
                    />
                    <ActionItem 
                      label="Pending proposals"
                      count={stats.pendingProposals}
                      onClick={() => handleSectionChange('proposals')}
                      loading={loading}
                    />
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Active partners</span>
                      <Badge variant="outline">{loading ? '...' : stats.partners}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Access Cards */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">QUICK ACCESS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <QuickAccessCard 
                  icon={Database}
                  title="Data Standards"
                  description="Manage API schema definitions"
                  stat={`${stats.apiStandards} active`}
                  onClick={() => handleSectionChange('standards')}
                />
                <QuickAccessCard 
                  icon={Tag}
                  title="ADF Elements"
                  description="Manage XML element definitions"
                  stat={`${stats.adfElements} elements`}
                  onClick={() => handleSectionChange('adf-elements')}
                />
                <QuickAccessCard 
                  icon={Library}
                  title="Field Library"
                  description="Reusable field definitions"
                  stat={`${stats.fieldLibrary} fields`}
                  onClick={() => handleSectionChange('field-library')}
                />
                <QuickAccessCard 
                  icon={List}
                  title="Lookups"
                  description="Standardized value lists"
                  stat={`${stats.lookupGroups} groups`}
                  onClick={() => handleSectionChange('lookups')}
                />
                <QuickAccessCard 
                  icon={FileText}
                  title="Documentation"
                  description="Guides and tutorials"
                  stat={`${stats.docArticles} articles`}
                  onClick={() => handleSectionChange('docs')}
                />
                <QuickAccessCard 
                  icon={BarChart3}
                  title="Analytics"
                  description="Usage insights and metrics"
                  stat="View dashboard"
                  onClick={() => handleSectionChange('analytics')}
                />
              </div>
            </div>

            {/* Quick Links */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium">Quick Links</CardTitle>
                <CardDescription>Jump to public pages and tools</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/api-standards">
                    <Database className="mr-2 h-3 w-3" />
                    API Standards
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/adf-standard">
                    <Tag className="mr-2 h-3 w-3" />
                    ADF Standard
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/api-standards/builder">
                    <Hammer className="mr-2 h-3 w-3" />
                    API Builder
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/adf-standard/builder">
                    <Hammer className="mr-2 h-3 w-3" />
                    ADF Builder
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/docs">
                    <FileText className="mr-2 h-3 w-3" />
                    Documentation
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/proposals">
                    <Lightbulb className="mr-2 h-3 w-3" />
                    Proposals
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/partners">
                    <Handshake className="mr-2 h-3 w-3" />
                    Partners
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <>
      <NoIndex />
      <AdminLayout>
        {renderContent()}
      </AdminLayout>
    </>
  );
}

// Compact stat card component
function StatCard({ 
  icon: Icon, 
  value, 
  label, 
  loading 
}: { 
  icon: React.ElementType; 
  value: number; 
  label: string; 
  loading: boolean;
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg shrink-0">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold">{loading ? '...' : value}</p>
          <p className="text-xs text-muted-foreground truncate">{label}</p>
        </div>
      </div>
    </Card>
  );
}

// Action item component
function ActionItem({ 
  label, 
  count, 
  onClick, 
  loading 
}: { 
  label: string; 
  count: number; 
  onClick: () => void; 
  loading: boolean;
}) {
  const hasItems = count > 0;
  
  return (
    <div 
      className={`flex items-center justify-between py-2 ${hasItems ? 'cursor-pointer hover:bg-muted/50 -mx-3 px-3 rounded-md' : ''}`}
      onClick={hasItems ? onClick : undefined}
    >
      <div className="flex items-center gap-2">
        {hasItems ? (
          <AlertCircle className="h-4 w-4 text-amber-500" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        )}
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={hasItems ? "default" : "secondary"}>
          {loading ? '...' : count}
        </Badge>
        {hasItems && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
      </div>
    </div>
  );
}

// Quick access card component
function QuickAccessCard({ 
  icon: Icon, 
  title, 
  description, 
  stat, 
  onClick 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  stat: string;
  onClick: () => void;
}) {
  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer" 
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="p-2 bg-primary/10 rounded-lg w-fit">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
        <CardTitle className="text-base mt-3">{title}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <Badge variant="secondary" className="text-xs">{stat}</Badge>
      </CardContent>
    </Card>
  );
}
